import { Upload } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  TextField,
  DialogActions,
  Chip,
  Autocomplete,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormLabel } from '@/components/common/FormLabel';
import { SimulationDataSetDto } from '@/api/ApiClient';

type ReaxffUploadDialogProps = {
  isUploadModalOpen: boolean;
  setIsUploadModalOpen: (open: boolean) => void;
  handleUploadSubmit: (selectedFile: File, metaData: SimulationDataSetDto) => Promise<void>;
};

export function ReaxffUploadDialog({
  isUploadModalOpen,
  setIsUploadModalOpen,
  handleUploadSubmit,
}: ReaxffUploadDialogProps) {
  const [author, setAutor] = useState('');
  const [elements, setElements] = useState<string[]>([]);
  const [year, setYear] = useState(2025);
  const [doi, setDoi] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const parts = file?.name.split('_');
      if (parts.length > 3) {
        setAutor(parts[1]);
        setElements(parts.slice(2, -1));
        setYear(parseInt(parts[parts.length - 1]));
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async () => {
    if (selectedFile) {
      setIsUploading(true);
      const metaData: SimulationDataSetDto = {
        simulationMethod: 'ReaxFF',
        filename: selectedFile.name,
        elements: elements.join(','),
        fileLink: '',
        mainAuthor: author,
        year: year,
        doi: doi,
      };
      try {
        await handleUploadSubmit(selectedFile, metaData);
      } finally {
        setIsUploading(false);
        closeDialog();
      }
    }
  };

  function closeDialog(): void {
    setAutor('');
    setElements([]);
    setYear(2025);
    setDoi('');
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  }

  return (
    <Dialog open={isUploadModalOpen} onClose={closeDialog}>
      <DialogTitle>Upload ReaxFF File</DialogTitle>
      {isUploading ? (
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            Uploading file...
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              padding: 2,
            }}
          >
            <CircularProgress size={50} />
          </Box>
        </DialogContent>
      ) : (
        <>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button variant="outlined" component="label" startIcon={<Upload />}>
                Select File
                <input type="file" hidden onChange={handleFileSelect} />
              </Button>
              {selectedFile ? <Box sx={{ mt: 1 }}>Selected file: {selectedFile.name}</Box> : null}
              <Box>
                <FormLabel required>Author</FormLabel>
                <TextField fullWidth value={author} margin="normal" onChange={(e) => setAutor(e.target.value)} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <FormLabel required>Elements</FormLabel>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]} // empty since we're using free input
                  value={elements}
                  onChange={(_, newValue) => setElements(newValue)}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const tagProps = getTagProps({ index });
                      const { key, ...rest } = tagProps;

                      return (
                        <Chip
                          key={key} // explicitly pass it
                          label={option}
                          variant="outlined"
                          {...rest}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      key="element-input" // avoids MUI key warning on dynamic resets
                      variant="outlined"
                      label=""
                      placeholder="Press Enter to add"
                    />
                  )}
                />
              </Box>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={year}
                margin="normal"
                onChange={(e) => setYear(parseInt(e.target.value) || 0)}
              />
              <TextField fullWidth label="DOI" value={doi} margin="normal" onChange={(e) => setDoi(e.target.value)} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!selectedFile}>
              Upload
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
